import React, { useState, useRef, useEffect } from 'react';
import { Command, Send, RefreshCw, Loader } from 'lucide-react';
import { GripVertical } from 'lucide-react';
import { io } from 'socket.io-client';
import MarkdownRenderer from './MarkdownRenderer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/Select';
import DynamicWindow from './DynamicWindow';

const SOCKET_EVENTS = {
  orchestratorEvents: {
    connect: (setIsConnected, setError) => () => {
      console.log('Socket Connected to Orchestrator');
      setIsConnected(true);
      setError(null);
    },
    disconnect: (setIsConnected, socketRef) => (reason) => {
      console.log('Socket Disconnected from Orchestrator:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect' && socketRef.current) {
        socketRef.current.connect();
      }
    },
    typing: (setIsTyping) => () => setIsTyping(true),
    stop_typing: (setIsTyping) => () => setIsTyping(false),
    error: (setError) => (err) => {
      console.error('Socket Error from Orchestrator:', err);
      setError('Connection error with orchestrator. Please try again.');
    },
  },
};

const ChatbotWidget = ({ darkMode }) => {
  const [messages, setMessages] = useState([
    { type: 'bot', text: 'Hello!' },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedModel, setSelectedModel] = useState('pirate');
  const [windowContent, setWindowContent] = useState(null);
  const [windowMode, setWindowMode] = useState('default');
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const socketIoRef = useRef(null); // For io.isadora.ai (orchestrator)
  const [leftWindowWidth, setLeftWindowWidth] = useState(40); // Default 40%
  const dragRef = useRef(null);
  const containerRef = useRef(null);
  const [retrying, setRetrying] = useState(false);

  const API_CONFIG = {
    CHAT_API: 'https://xyz.isadora.ai',
  };

  useEffect(() => {
    // Initialize Socket.IO connection to io.isadora.ai (orchestrator)
    socketIoRef.current = io(API_CONFIG.CHAT_API, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 600000,
      withCredentials: true,
      autoConnect: true,
      path: '/socket.io',
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      forceNew: true,
    });

    // Set up event handlers for orchestrator socket
    const { orchestratorEvents } = SOCKET_EVENTS;

    Object.entries(orchestratorEvents).forEach(([event, handlerCreator]) => {
      socketIoRef.current.on(event, handlerCreator(setIsConnected, setError, setIsTyping));
    });

    // Cleanup function
    return () => {
      if (socketIoRef.current) {
        Object.keys(orchestratorEvents).forEach((event) => {
          socketIoRef.current.off(event);
        });
        socketIoRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleWindowUpdate = (message) => {
      console.log('Window update message received:', message);
      if (message.event === 'window_update') {
        setWindowContent(message.data.content);
        if (message.data.mode) setWindowMode(message.data.mode);
      }
    };

    if (socketIoRef.current) {
      socketIoRef.current.on('window_update', handleWindowUpdate);
    }

    return () => {
      if (socketIoRef.current) {
        socketIoRef.current.off('window_update', handleWindowUpdate);
      }
    };
  }, []);

  useEffect(() => {
    if (socketIoRef.current) {
      const pingInterval = setInterval(() => {
        if (socketIoRef.current.connected) {
          socketIoRef.current.emit('ping');
        }
      }, 30000); // 30 seconds

      return () => clearInterval(pingInterval);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const savedMessages = localStorage.getItem('chatHistory');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }

    const savedModel = localStorage.getItem('selectedModel');
    if (savedModel) {
      setSelectedModel(savedModel);
    }

    const savedWindowContent = localStorage.getItem('windowContent');
    if (savedWindowContent) {
      setWindowContent(JSON.parse(savedWindowContent));
    }

    const savedWindowMode = localStorage.getItem('windowMode');
    if (savedWindowMode) {
      setWindowMode(savedWindowMode);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('chatHistory', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('selectedModel', selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    if (windowContent) {
      localStorage.setItem('windowContent', JSON.stringify(windowContent));
    }
  }, [windowContent]);

  useEffect(() => {
    localStorage.setItem('windowMode', windowMode);
  }, [windowMode]);

  useEffect(() => {
    const savedWidth = localStorage.getItem('leftWindowWidth');
    if (savedWidth) {
      setLeftWindowWidth(parseFloat(savedWidth));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('leftWindowWidth', leftWindowWidth.toString());
  }, [leftWindowWidth]);

  const handleDragStart = (e) => {
    e.preventDefault();
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  };

  const handleDrag = (e) => {
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      const newLeftWidth = (mouseX / containerWidth) * 100;

      // Limit the resize between 20% and 80%
      if (newLeftWidth >= 20 && newLeftWidth <= 80) {
        setLeftWindowWidth(newLeftWidth);
      }
    }
  };

  const handleDragEnd = () => {
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleDragEnd);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputMessage.trim() && !isTyping && !isLoading) {
      const userMessage = {
        type: 'user',
        text: inputMessage.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputMessage('');
      setError(null);
      setIsTyping(true);
      setIsLoading(true);

      try {
        if (!navigator.onLine) {
          throw new Error('No internet connection');
        }

        socketIoRef.current?.emit('typing', { typing: true });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);

        const response = await fetch(`${API_CONFIG.CHAT_API}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Origin: window.location.origin,
          },
          credentials: 'include',
          mode: 'cors',
          signal: controller.signal,
          body: JSON.stringify({
            message: userMessage.text,
            conversation_id: localStorage.getItem('conversationId') || Date.now().toString(),
            function: selectedModel,
            window_mode: windowMode,
            current_window_content: windowContent,
          }),
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const botResponse = {
          type: 'bot',
          text: data.response || data.message,
        };

        setMessages((prev) => [...prev, botResponse]);
      } catch (error) {
        console.error('Error:', error);
        let errorMessage = 'Failed to get response. Please try again.';

        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (!navigator.onLine) {
          errorMessage = 'No internet connection. Please check your network.';
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Network error. Please check your connection.';
        }

        setError(errorMessage);
        setMessages((prev) => [
          ...prev,
          {
            type: 'bot',
            text: 'Sorry, I encountered an error: ' + errorMessage,
          },
        ]);
      } finally {
        setIsLoading(false);
        setIsTyping(false);
        socketIoRef.current?.emit('stop_typing', { typing: false });
      }
    }
  };

  const clearChat = () => {
    setMessages([{ type: 'bot', text: 'Hello!' }]);
    setWindowContent(null);
    setWindowMode('default');
    localStorage.removeItem('chatHistory');
    localStorage.removeItem('conversationId');
    localStorage.removeItem('windowContent');
    localStorage.removeItem('windowMode');
  };

  return (
    <div className="min-h-screen bg-gradient animate-gradient">
      <div className="container mx-auto px-4 py-8">
        <div ref={containerRef} className="flex relative w-full max-w-7xl mx-auto">
          {/* Dynamic Window */}
          <div
            className="flex-shrink-0 transition-all duration-200 ease-in-out"
            style={{ width: `${leftWindowWidth}%` }}
          >
            <DynamicWindow darkMode={darkMode} />;
          </div>

          {/* Resize Handle */}
          <div
            ref={dragRef}
            className="flex items-center justify-center w-4 cursor-col-resize relative z-10"
            onMouseDown={handleDragStart}
          >
            <div className="h-full w-0.5 bg-gray-200 dark:bg-gray-700" />
            <div className="absolute p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
              <GripVertical className="w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Chatbot Card */}
          <div
            className="flex-shrink-0 transition-all duration-200 ease-in-out"
            style={{ width: `${100 - leftWindowWidth - 1}%` }}
          >
            <div className="rounded-xl shadow-glow bg-white/90 dark:bg-gray-800/90 overflow-hidden h-full">
              {/* Header */}
              <div className="p-4 border-b dark:border-gray-700 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h1 className="text-xl font-semibold text-gray-800 dark:text-white animate-text-glow">
                      <Command className="w-6 h-6" />
                    </h1>
                    <Select
                      value={selectedModel}
                      onValueChange={setSelectedModel}
                      className="animate-fade-in"
                    >
                      <SelectTrigger className="w-[180px] bg-white/50 dark:bg-gray-700/50 border-none hover:bg-white/70 dark:hover:bg-gray-700/70 transition-colors">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent className="bg-white/90 dark:bg-gray-800/90">
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="security">Lochagos</SelectItem>
                        <SelectItem value="pirate">Pirate</SelectItem>
                        <SelectItem value="shakespeare">Shakespeare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        isConnected ? 'animate-pulse-green' : 'animate-pulse-red'
                      }`}
                      title={isConnected ? 'Connected' : 'Disconnected'}
                    />
                    <button
                      onClick={clearChat}
                      className="p-2 rounded-full hover:bg-white/20 dark:hover:bg-gray-700/50 transition-all duration-300 hover:scale-110"
                      aria-label="Clear chat"
                    >
                      <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div
                ref={chatContainerRef}
                className="h-[calc(100vh-16rem)] overflow-y-auto p-6 bg-gradient-to-b from-gray-50/50 to-gray-100/50 dark:from-gray-900/50 dark:to-gray-800/50"
              >
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`
                          max-w-[70%] px-4 py-2 rounded-lg shadow-glow
                          ${message.type === 'user' 
                            ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white animate-slide-left' 
                            : 'bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-white animate-slide-right'}
                          transform hover:scale-[1.02] transition-all duration-300
                        `}
                      >
                        <MarkdownRenderer content={message.text} />
                      </div>
                    </div>
                  ))}
                  {isTyping && (
                    <div className="flex items-center text-gray-500 dark:text-gray-400 animate-fade-in">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="ml-2">AI is thinking...</span>
                    </div>
                  )}
                  {error && (
                    <div className="text-red-500 dark:text-red-400 text-sm mt-2 animate-shake">
                      {error}
                    </div>
                  )}
                  {retrying && (
                    <div className="text-amber-500 dark:text-amber-400 text-sm animate-pulse flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Reconnecting to server...</span>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input Area */}
              <form
                onSubmit={handleSendMessage}
                className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600
                             bg-white/50 dark:bg-gray-700/50
                             text-gray-900 dark:text-white
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                             focus:border-transparent transition-all duration-300
                             hover:shadow-glow"
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim() || isTyping || isLoading}
                    className={`
                      px-4 py-2 rounded-lg flex items-center gap-2 transform hover:scale-105
                      transition-all duration-300 hover:shadow-glow
                      ${inputMessage.trim() && !isTyping && !isLoading
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'}
                    `}
                  >
                    {isLoading ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotWidget;